import { z } from 'zod';
import { cuid } from './primitives.js';

/** Ciclo de vida da versao. RASCUNHO e editavel; ENVIADA e congelada (snapshot, itens, PDF com hash); APROVADA e a escolhida. */
export const statusVersao = z.enum(['RASCUNHO', 'ENVIADA', 'APROVADA', 'DESCARTADA', 'IMPORTADA']);
export type StatusVersaoDto = z.infer<typeof statusVersao>;

export const origemOrcamento = z.enum(['APP', 'IMPORTADO_XARP']);
export type OrigemOrcamentoDto = z.infer<typeof origemOrcamento>;

export const eventoSituacao = z.enum(['SITUACAO', 'VERSAO_CRIADA', 'VERSAO_ENVIADA', 'VERSAO_APROVADA', 'VERSAO_DESCARTADA']);
export type EventoSituacaoDto = z.infer<typeof eventoSituacao>;

/** Numero exibido da proposta: `33392-v2`. */
export function numeroExibido(numero: number, versao: number): string {
  return `${numero}-v${versao}`;
}

/** `:n` das rotas de versao. */
export const versaoParam = z.object({ id: cuid, n: z.coerce.number().int().min(1).max(9999) });
export type VersaoParam = z.infer<typeof versaoParam>;

/** GET /orcamentos/:id/versoes/comparar?a=&b= (numeros das versoes). */
export const compararVersoesSchema = z.object({
  a: z.coerce.number().int().min(1).max(9999),
  b: z.coerce.number().int().min(1).max(9999),
});
export type CompararVersoesQuery = z.infer<typeof compararVersoesSchema>;

/**
 * Uma linha da aba Versoes. Sem custo: so total, lucro % (exibido) e faixa, que o VENDEDOR pode ver (DP1);
 * `lucroValor` (R$) so com custo.ver.
 */
export interface VersaoResumoResponse {
  id: string;
  numero: number;
  /** `33392-v2` */
  numeroExibido: string;
  status: StatusVersaoDto;
  origem: OrigemOrcamentoDto;
  /** true = v1 criada pela migration (orcamento anterior as versoes; sem PDF congelado). */
  migrada: boolean;
  /** Data da operacao (vigencias). */
  data: string;
  descricao: string | null;
  /** Total do documento (dinheiro, 2 casas). */
  total: string;
  quantidade: string;
  /** Lucro exibido (fracao, 4 casas) e faixa do motor. */
  lucro: string | null;
  faixa: 'green' | 'orange' | 'red' | null;
  /** So com custo.ver. */
  lucroValor?: string;
  consideraFreteLucro: boolean;
  itensTotal: number;
  /** Validade da versao (gravada no envio). Nula em rascunho. */
  validaAte: string | null;
  validadeDias: number;
  /** Derivado: versao enviada e nao aprovada com `validaAte` anterior a hoje (nao e uma situacao). */
  vencido: boolean;
  enviadaEm: string | null;
  enviadaPorId: string | null;
  enviadaPorNome: string | null;
  aprovadaEm: string | null;
  aprovadaPorNome: string | null;
  descartadaEm: string | null;
  /** Numero da versao da qual esta foi copiada. */
  criadaDeNumero: number | null;
  criadaPorNome: string | null;
  createdAt: string;
  /** sha256 do PDF guardado (versao enviada pelo app); o download confere antes de entregar. */
  pdfSha256: string | null;
  /** true = versao do cabecalho que vale para lista, kanban, margens e exportacoes. */
  referencia: boolean;
  /** true em versao nao editavel (tudo, menos RASCUNHO). */
  somenteLeitura: boolean;
}

/** Evento da linha do tempo (situacoes do pipeline e ciclo das versoes). */
export interface OrcamentoEventoResponse {
  id: string;
  evento: EventoSituacaoDto;
  versaoNumero: number | null;
  /** Situacao do cabecalho antes e depois (iguais nos eventos de versao que nao a mudam). */
  de: string | null;
  para: string;
  motivo: string | null;
  usuarioId: string | null;
  usuarioNome: string | null;
  createdAt: string;
}

export type SituacaoItemComparado = 'INCLUIDO' | 'REMOVIDO' | 'ALTERADO' | 'MANTIDO';

export interface ItemComparadoLado {
  seq: number;
  quantidade: string;
  precoUnitario: string;
  total: string;
  /** Lucro do item (fracao, 4 casas) e faixa. */
  lucro: string | null;
  faixa: 'green' | 'orange' | 'red' | null;
  /** So com custo.ver. */
  custoUnitario?: string | null;
  /** So com custo.ver. */
  lucroValor?: string | null;
}

export interface ItemComparado {
  itemId: string;
  codigo: string;
  descricao: string;
  situacao: SituacaoItemComparado;
  /** Lado da versao `a` (ausente em INCLUIDO) e da `b` (ausente em REMOVIDO). */
  a?: ItemComparadoLado;
  b?: ItemComparadoLado;
  /** b - a. Dinheiro 2 casas; lucro em pontos (fracao, 4 casas). Ausente em INCLUIDO/REMOVIDO (so um lado). */
  delta?: { quantidade: string; precoUnitario: string; total: string; lucro: string | null };
}

export interface TotaisComparados {
  total: string;
  receitaItens: string;
  frete: string;
  lucro: string | null;
  faixa: 'green' | 'orange' | 'red' | null;
  /** So com custo.ver. */
  lucroValor?: string;
}

/** GET /orcamentos/:id/versoes/comparar: tudo calculado no servidor, sobre os valores GRAVADOS das duas versoes. */
export interface ComparacaoVersoesResponse {
  orcamentoId: string;
  a: VersaoResumoResponse;
  b: VersaoResumoResponse;
  itens: ItemComparado[];
  totais: { a: TotaisComparados; b: TotaisComparados; delta: { total: string; lucro: string | null; lucroValor?: string } };
  resumo: { incluidos: number; removidos: number; alterados: number; mantidos: number };
}
